import React, { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ShareIcon from '@mui/icons-material/Share';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import { PageWrapper } from '../components/PageWrapper';
import { CompanyLocationsSection } from '../components/CompanyLocationsSection';
import { CompanyCvorsSection } from '../components/CompanyCvorsSection';
import { baseUrl } from '../customDataProvider';

// This page edits Tenant fields — the former separate CompanyProfile table was
// merged into Tenant since it was a strict 1:1 with no independent lifecycle.
interface CompanyProfile {
  code: string;
  name: string | null;
  timezone: string;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string | null;
  linkToAccountAddress: boolean;
  phone: string | null;
  linkToAccountPhone: boolean;
  facebookUrl: string | null;
  twitterUrl: string | null;
  linkedInUrl: string | null;
  hasIftaLicence: boolean | null;
  iftaLicenceOrigin: string | null;
  iftaLicenceNumber: string | null;
  iftaLicenceExpiryDate: string | null;
  createdAt: string;
  updatedAt: string;
}

const PROFILE_PATCH_FIELDS: (keyof CompanyProfile)[] = [
  'name',
  'timezone',
  'website',
  'phone',
  'linkToAccountPhone',
  'facebookUrl',
  'twitterUrl',
  'linkedInUrl',
  'hasIftaLicence',
  'iftaLicenceOrigin',
  'iftaLicenceNumber',
  'iftaLicenceExpiryDate',
];

const TIMEZONE_CHOICES = [
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)' },
  { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'America/Halifax', label: 'Atlantic Time (Halifax)' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (St. John\'s)' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
];

const getAccessToken = () => localStorage.getItem('accessToken') || '';
const getTenantCode = () => {
  const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
  if (!tenantsEnabled) return 'default';
  const currentTenant = localStorage.getItem('currentTenant');
  try { return currentTenant ? JSON.parse(currentTenant).code : 'default'; } catch { return 'default'; }
};

const displayValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : 'Not set';
};

const SectionCard = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const ReadField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

export const CompanyProfilePage = () => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<CompanyProfile>>({});
  const notify = useNotify();

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  useEffect(() => {
    if (companyProfile) {
      setFormData(companyProfile);
    }
  }, [companyProfile]);

  const authHeaders = () => {
    const tenantCode = getTenantCode();
    return {
      Authorization: `Bearer ${getAccessToken()}`,
      Accept: 'application/json',
      'x-tenant-code': tenantCode,
    };
  };

  const loadCompanyProfile = async () => {
    try {
      setLoading(true);
      const tenantCode = getTenantCode();
      const res = await fetch(`${baseUrl}/tenants/${tenantCode}`, {
        headers: authHeaders(),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      setCompanyProfile(result);
    } catch (error) {
      console.error('Error loading company profile:', error);
      notify('Failed to load company profile', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const tenantCode = getTenantCode();
      const payload = PROFILE_PATCH_FIELDS.reduce((acc, key) => {
        if (formData[key] !== undefined) {
          (acc as Record<string, unknown>)[key] = formData[key];
        }
        return acc;
      }, {} as Partial<CompanyProfile>);

      const res = await fetch(`${baseUrl}/tenants/${tenantCode}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      setCompanyProfile(result);
      setEditMode(false);
      notify('Company profile updated successfully', { type: 'success' });
    } catch (error) {
      console.error('Error saving company profile:', error);
      notify('Failed to save company profile', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper title="Company Profile">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (!companyProfile) {
    return (
      <PageWrapper title="Company Profile">
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <BusinessIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unable to Load Company Profile
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
                Something went wrong loading your company profile. Please try refreshing the page, or contact support if the problem persists.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Company Profile">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        {!editMode ? (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditMode(true)}>
            Edit
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                setEditMode(false);
                setFormData(companyProfile);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        )}
      </Box>

      <SectionCard icon={<BusinessIcon color="primary" />} title="Company Information">
        {editMode ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.name || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Timezone"
                value={formData.timezone || 'America/Toronto'}
                onChange={(e) => setFormData((prev) => ({ ...prev, timezone: e.target.value }))}
                SelectProps={{ native: true }}
              >
                {TIMEZONE_CHOICES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ReadField label="Company Name" value={displayValue(companyProfile.name)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadField label="Website" value={displayValue(companyProfile.website)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadField
                label="Timezone"
                value={TIMEZONE_CHOICES.find((tz) => tz.value === companyProfile.timezone)?.label || companyProfile.timezone}
              />
            </Grid>
          </Grid>
        )}
      </SectionCard>

      <SectionCard icon={<LocationOnIcon color="primary" />} title="Locations">
        <CompanyLocationsSection />
      </SectionCard>

      <SectionCard icon={<PhoneIcon color="primary" />} title="Phone">
        {editMode ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.linkToAccountPhone ?? false}
                    onChange={(e) => setFormData((prev) => ({ ...prev, linkToAccountPhone: e.target.checked }))}
                  />
                }
                label="Link company phone number to your account phone"
              />
            </Grid>
            {formData.linkToAccountPhone ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Phone number is linked to your account phone and updates automatically.
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="(123) 456-7890"
                />
              </Grid>
            )}
          </Grid>
        ) : (
          <ReadField
            label="Phone Number"
            value={companyProfile.linkToAccountPhone ? 'Linked to your account phone' : displayValue(companyProfile.phone)}
          />
        )}
      </SectionCard>

      <SectionCard icon={<ShareIcon color="primary" />} title="Social Media">
        {editMode ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Facebook URL"
                value={formData.facebookUrl || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, facebookUrl: e.target.value }))}
                placeholder="https://www.facebook.com/yourcompany"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Twitter URL"
                value={formData.twitterUrl || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, twitterUrl: e.target.value }))}
                placeholder="https://twitter.com/yourcompany"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="LinkedIn URL"
                value={formData.linkedInUrl || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, linkedInUrl: e.target.value }))}
                placeholder="https://www.linkedin.com/company/yourcompany"
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ReadField label="Facebook" value={displayValue(companyProfile.facebookUrl)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadField label="Twitter" value={displayValue(companyProfile.twitterUrl)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadField label="LinkedIn" value={displayValue(companyProfile.linkedInUrl)} />
            </Grid>
          </Grid>
        )}
      </SectionCard>

      <SectionCard icon={<BadgeIcon color="primary" />} title="CVOR/NSC">
        <CompanyCvorsSection />
      </SectionCard>

      <SectionCard icon={<LanguageIcon color="primary" />} title="IFTA Licence">
        {editMode ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasIftaLicence ?? false}
                    onChange={(e) => setFormData((prev) => ({ ...prev, hasIftaLicence: e.target.checked }))}
                  />
                }
                label="Do you have an IFTA licence?"
              />
            </Grid>
            {formData.hasIftaLicence && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="IFTA Licence Origin (Province/State)"
                    value={formData.iftaLicenceOrigin || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, iftaLicenceOrigin: e.target.value }))}
                    placeholder="Select province..."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="IFTA Licence Number"
                    value={formData.iftaLicenceNumber || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, iftaLicenceNumber: e.target.value }))}
                    placeholder="Enter licence number"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="IFTA Licence Expiry Date"
                    type="date"
                    value={formData.iftaLicenceExpiryDate ? new Date(formData.iftaLicenceExpiryDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, iftaLicenceExpiryDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        ) : companyProfile.hasIftaLicence ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ReadField label="Origin" value={displayValue(companyProfile.iftaLicenceOrigin)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadField label="Licence Number" value={displayValue(companyProfile.iftaLicenceNumber)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ReadField
                label="Expiry Date"
                value={
                  companyProfile.iftaLicenceExpiryDate
                    ? new Date(companyProfile.iftaLicenceExpiryDate).toLocaleDateString()
                    : 'Not set'
                }
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No IFTA licence.
          </Typography>
        )}
      </SectionCard>
    </PageWrapper>
  );
};
